/**
 * appointmentHelpers — 예약 상세 공통 헬퍼 함수
 *
 * BookingDetailContent / useBookingDetail에서 공유
 */

import {
  PhoneIncoming,
  MessageSquare,
  Edit,
  CalendarDays,
  ArrowRight,
  Calendar,
  Users,
  Bell,
  Check,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { createElement } from "react";

// ===== 접수 경로 =====

export function getSourceLabel(source?: string) {
  switch (source) {
    case "CALL":
      return "AI전화";
    case "MSG":
      return "메시지";
    case "MANUAL":
      return "수기입력";
    default:
      return source || "수기";
  }
}

export function getSourceIcon(source?: string) {
  switch (source) {
    case "CALL":
      return createElement(PhoneIncoming, { className: "h-3.5 w-3.5" });
    case "MSG":
      return createElement(MessageSquare, { className: "h-3.5 w-3.5" });
    default:
      return createElement(Edit, { className: "h-3.5 w-3.5" });
  }
}

// ===== 포맷팅 =====

export function formatDuration(seconds?: number) {
  if (!seconds) return null;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}분 ${s}초` : `${s}초`;
}

export function formatPrice(
  min?: number,
  max?: number,
  enabled?: boolean,
) {
  if (enabled === false) return "병원에 문의";
  if (min && max && min !== max)
    return `${min.toLocaleString()}원 ~ ${max.toLocaleString()}원`;
  if (min) return `${min.toLocaleString()}원`;
  if (max) return `${max.toLocaleString()}원`;
  return null;
}

// ===== 고객 정보 =====

export function getGenderLabel(gender?: string) {
  switch (gender) {
    case "MALE":
      return "M";
    case "FEMALE":
      return "F";
    default:
      return null;
  }
}

export function getAgeFromBirthDate(birthDate?: string) {
  if (!birthDate || birthDate.length !== 6) return null;
  const yy = parseInt(birthDate.substring(0, 2));
  const year = yy >= 0 && yy <= 30 ? 2000 + yy : 1900 + yy;
  const now = new Date().getFullYear();
  return now - year;
}

// ===== 상태 관련 =====

export function getStatusColor(status: string) {
  switch (status) {
    case "REQUESTED":
    case "CHECKING":
      return "border-l-orange-500";
    case "CONFIRMED":
      return "border-l-green-500";
    case "CANCELLED":
      return "border-l-red-500";
    case "COMPLETED":
      return "border-l-blue-500";
    case "NO_SHOW":
      return "border-l-gray-800";
    default:
      return "border-l-gray-300";
  }
}

// ===== 진행이력 아이콘 =====

export function getHistoryIcon(changeType: string) {
  const iconClass = "h-4 w-4";
  switch (changeType) {
    case "CREATED":
      return createElement(CalendarDays, {
        className: cn(iconClass, "text-info"),
      });
    case "STATUS_CHANGE":
      return createElement(ArrowRight, {
        className: cn(iconClass, "text-success"),
      });
    case "RESCHEDULE":
      return createElement(Calendar, {
        className: cn(iconClass, "text-warning"),
      });
    case "ASSIGN":
      return createElement(Users, {
        className: cn(iconClass, "text-purple-500"),
      });
    case "MEMO":
      return createElement(MessageSquare, {
        className: cn(iconClass, "text-muted-foreground"),
      });
    case "REMINDER_SENT":
      return createElement(Bell, {
        className: cn(iconClass, "text-warning"),
      });
    default:
      return createElement(Check, {
        className: cn(iconClass, "text-muted-foreground"),
      });
  }
}
